import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import * as Utils from '../../../../../services/utilityFunctions';
import translate from '../../../../../commonComponents/translate';
import ProductImage from '../pack/ProductImage';
import ProductProp from '../../../propTypes/ProductProp';
import QuantityModal from './QuantityModal';
import Switch from '../../../../../commonComponents/forms/inputs/Switch';
import PlanProp from '../../../propTypes/PlanProp';
import { isFeatureAvailable } from '../../../../../services/cartCalculations';

function ProductItem({
  t,
  product,
  isInCart,
  isSubscriptionEdit = true,
  onClickAdd,
  onClickRemove,
  onOpenDescriptionModal,
  isPack = false,
  selectedPlan,
  hasPermissionToChange,
}) {
  const [showQuantityModal, setShowQuantityModal] = useState(false);

  const handleProductAdd = () => {
    if (!product.allowQuantity) {
      onClickAdd({ product, quantity: 1 });
    } else {
      setShowQuantityModal(true);
    }
  };

  const handleClick = evt => {
    evt.stopPropagation();
    const cb = isInCart ? onClickRemove : handleProductAdd;
    cb(product);
  };

  const handleQuantityModalClose = quantity => {
    setShowQuantityModal(false);
    if (quantity) {
      onClickAdd({ product, quantity });
    }
  };

  const openDescriptionModal = evt => {
    if (evt.target.tagName !== 'INPUT' && evt.target.tagName !== 'LABEL') {
      onOpenDescriptionModal(product);
    }
  };

  const { type, isBought, isFreeTrial, isDefaultForSubscription } = product;

  const isSubscription =
    type === Utils.PRODUCT_TYPE.SUBSCRIPTION ||
    type === Utils.PRODUCT_TYPE.PREMIUM_SUBSCRIPTION;
  const featureAvailable = isFeatureAvailable(product, selectedPlan);
  const showBought = isBought && type === Utils.PRODUCT_TYPE.SINGLE_BUY;
  const markAsBought =
    (showBought && isSubscriptionEdit) ||
    (showBought && !isSubscriptionEdit && !isFreeTrial);
  const tempAddText = markAsBought ? 'bought' : 'add';
  const addText = isSubscription ? 'subscribe' : tempAddText;
  const removeText = isSubscription ? 'unsubscribe' : 'remove';

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div
        role="button"
        tabIndex={0}
        onClick={openDescriptionModal}
        className="product-item"
      >
        <ProductImage
          className="product-item-img"
          product={{
            ...product,
            belongsToPack: isPack,
            isInCart,
            isSubscription,
            showBought,
          }}
          light={isPack}
        />
        <div className="product-name">{product.name}</div>
        <div className="product-item-add">
          {isSubscription ? (
            <Switch
              onClick={handleClick}
              checked={isInCart && featureAvailable}
              id={product._id}
              disabledModuleItem={!featureAvailable || !hasPermissionToChange}
            />
          ) : (
            <button
              disabled={
                showBought ||
                !featureAvailable ||
                (!isSubscriptionEdit && isDefaultForSubscription)
              }
              type="button"
              className={cx(
                'btn m-btn--pill m-btn--air btn-sm m-btn m-btn--custom',
                { 'cat-btn-available': !isInCart && !showBought },
                { 'cat-btn-remove': isInCart && !showBought },
                { 'cat-btn-remove-no-hover': showBought },
              )}
              onClick={handleClick}
            >
              {isInCart ? t(removeText) : t(addText)}
            </button>
          )}
        </div>
      </div>
      {showQuantityModal && (
        <QuantityModal onClose={handleQuantityModalClose} />
      )}
    </>
  );
}

ProductItem.propTypes = {
  t: PropTypes.func.isRequired,
  product: PropTypes.shape(ProductProp.propType),
  isInCart: PropTypes.bool.isRequired,
  isSubscriptionEdit: PropTypes.bool,
  onClickAdd: PropTypes.func.isRequired,
  onClickRemove: PropTypes.func.isRequired,
  onOpenDescriptionModal: PropTypes.func.isRequired,
  isPack: PropTypes.bool,
  selectedPlan: PropTypes.shape(PlanProp.propType).isRequired,
  hasPermissionToChange: PropTypes.bool,
};

ProductItem.defaultProps = {
  isSubscriptionEdit: true,
  product: ProductProp.defaultProp,
  isPack: false,
  hasPermissionToChange: true,
};

export default translate(ProductItem);
